import React, { useEffect, useMemo, useRef, useState } from "react";
import { StyledDropdown } from "./StyledDropdown";

type DropdownOption = string | { label: string; value: string };

const normalizeOption = (option: DropdownOption) =>
  typeof option === "string"
    ? { label: option, value: option }
    : option;

const Dropdown = ({
  options,
  label,
  onClick,
  placeholder,
  className,
  defaultValue,
  multiple,
  value,
}: {
  options: DropdownOption[];
  label?: string;
  value?: string | string[];
  placeholder?: string;
  onClick: (v: string | string[]) => void;
  className?: string;
  defaultValue?: string | string[] | null;
  multiple?: boolean;
}) => {
  const normalizedOptions = useMemo(
    () => options.map(normalizeOption),
    [options]
  );

  const getOptionLabel = (optionValue: string) =>
    normalizedOptions.find((option) => option.value === optionValue)?.label ??
    optionValue;

  const [drawerOpened, setDrawerOpened] = useState(false);
  const [selected, setSelected] = useState<string | string[]>(() => {
    if (multiple) {
      if (Array.isArray(value)) return value.filter((v) => v.length);
      if (Array.isArray(defaultValue))
        return defaultValue.filter((v) => v.length);
      return [];
    }
    if (Array.isArray(defaultValue))
      return defaultValue.filter((v) => v.length);
    return defaultValue ?? "";
  });

  useEffect(() => {
    if (multiple) {
      if (Array.isArray(value)) {
        setSelected(value.filter((v) => v.length));
      } else if (value === undefined || value === null) {
        setSelected(
          Array.isArray(defaultValue)
            ? defaultValue.filter((v) => v.length)
            : []
        );
      }
      return;
    }

    if (value) {
      setSelected(
        Array.isArray(value) ? value.filter((v) => v.length) : value ?? ""
      );
    } else {
      setSelected(
        Array.isArray(defaultValue)
          ? defaultValue.filter((v) => v.length)
          : defaultValue ?? ""
      );
    }
  }, [value, defaultValue, multiple]);

  const handleClick = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (
      e.target.value === "Seleziona un opzione" ||
      e.target.value === placeholder ||
      e.target.value === "Seleziona"
    ) {
      setSelected(e.target.value);
      onClick("");
    } else {
      setSelected(e.target.value);
      onClick(e.target.value);
    }
  };

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        drawerOpened &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDrawerOpened(false);
      }
    };

    document.addEventListener("click", handler);

    return () => {
      document.removeEventListener("click", handler);
    };
  }, [drawerOpened]);

  return (
    <StyledDropdown className={className} ref={dropdownRef}>
      {label && <h3>{label}</h3>}
      <div className="select">
        <p>
          {selected.length > 0
            ? Array.isArray(selected)
              ? selected.join(", ").length
                ? selected.map(getOptionLabel).join(", ")
                : "Seleziona"
              : getOptionLabel(selected)
            : placeholder ?? "Seleziona"}
        </p>
        {multiple ? (
          <>
            <button
              onClick={() => setDrawerOpened((p) => !p)}
              className="drawer-act"
            ></button>
            <div className={["drawer", drawerOpened ? "open" : ""].join(" ")}>
              <ul>
                {normalizedOptions.map((option) => (
                  <li
                    key={option.value}
                    onClick={() =>
                      setSelected((p) => {
                        const currentSelected = (
                          Array.isArray(p) ? p : p ? [p] : []
                        ).filter(Boolean);
                        const index = currentSelected.indexOf(option.value);
                        let newSelected = [...currentSelected];
                        if (index !== -1) {
                          newSelected.splice(index, 1);
                        } else {
                          newSelected.push(option.value);
                        }
                        onClick(newSelected);
                        return newSelected;
                      })
                    }
                    className={
                      Array.isArray(selected)
                        ? selected.includes(option.value)
                          ? "selected"
                          : ""
                        : selected === option.value
                        ? "selected"
                        : ""
                    }
                  >
                    {option.label}
                  </li>
                ))}
              </ul>
            </div>
          </>
        ) : (
          <select onChange={handleClick} value={selected}>
            <option>Seleziona un opzione</option>
            {normalizedOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}
        <div className="arr-wrapper">
          <span className="arr" />
        </div>
      </div>
    </StyledDropdown>
  );
};

export default Dropdown;
